import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import SalesProgressionTab from './Sales/SalesProgressionTab';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSalesOnboardingMap } from '../services/api/implementation/impl';
import { SalesList } from './SalesList';
import PreleadList from './PreleadList';
import CustomerList from './CustomerList';
import ConsolidatedList from './ConsolidatedList';
import UnderConsiderationList from './UnderConsiderationList';
import SalesDashboard from './Sales/SalesDashboard';
import SalesUnitInventory from './Sales/SalesUnitInventory';
import TargetsAndIncentives from './TargetsAndIncentives';
import PropertyDocuments from './PropertyDocuments';
import { useSidebar } from '../../shared/contexts/SidebarContext';
// import Modal from '../../shared/components/ui/Modal';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { isDominoEnabled } from '../../shared/utils/authStorage';

const salesHeaderNavClass = (active) =>
  `px-3 py-1.5 rounded-[8px] text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

const SalesMain = () => {
  const { id: projectId } = useParams();
  const { collapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const userVertical = Cookies.get('userVertical');
  const isManagementVertical = userVertical?.toLowerCase() === 'management';
  const showProgressionTab = !isManagementVertical && isDominoEnabled();
  const salesUserId = Number(Cookies.get('userId'));
  const isTotalLeadsRoute = location.pathname.includes('/builder/sales/total-leads');
  const status = location.state?.status;
  const projectIDFromState = location.state?.projectID ?? null;
  const userId = Cookies.get('userId');

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Main Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sales Main Viewed', {
      user_id: userId,
      project_id: projectId,
      initial_status: status,
      timestamp: new Date().toISOString()
    });
  }, []);

  // Helper function to get saved tab from localStorage
  const getSavedPrimaryTab = () => {
    try {
      return localStorage.getItem('salesMainPrimaryTab') || 'Details';
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return 'Details';
    }
  };

  // Helper function to save tab to localStorage
  const savePrimaryTab = (tab) => {
    try {
      localStorage.setItem('salesMainPrimaryTab', tab);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  // Helper function to get saved secondary tab from localStorage
  const getSavedSecondaryTab = () => {
    try {
      const t = localStorage.getItem('salesMainSecondaryTab') || 'Leads';
      if (t === 'Prequalified List') {
        localStorage.setItem('salesMainSecondaryTab', 'Leads');
        return 'Leads';
      }
      return t;
    } catch (error) {
      console.error('Error reading secondary tab from localStorage:', error);
      return 'Leads';
    }
  };

  // Helper function to save secondary tab to localStorage
  const saveSecondaryTab = (tab) => {
    try {
      localStorage.setItem('salesMainSecondaryTab', tab);
    } catch (error) {
      console.error('Error saving secondary tab to localStorage:', error);
    }
  };

  /* 
        Data fetching
      */

  /* 
        Data mutation
      */

  /*
        Hooks 
      */

  /* 
        Local state 
      */
  const [primaryTab, setPrimaryTab] = useState(getSavedPrimaryTab());
  const [secondaryTab, setSecondaryTab] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('/builder/sales/total-leads')) {
      return 'Leads';
    }
    // If status is passed, it's a filter value, not a tab name
    // So we should default to 'Leads' tab
    return status ? 'Leads' : getSavedSecondaryTab();
  });
  const [statusFilter, setStatusFilter] = useState(status || null);

  /** On total-leads URLs, drive lead temperature filter from the path; otherwise use dashboard/navigation state */
  const salesListStatusFilter = useMemo(() => {
    const p = location.pathname.replace(/\/$/, '');
    if (p.endsWith('/total-leads/hot')) return ['Hot'];
    if (p.endsWith('/total-leads/warm')) return ['Warm'];
    if (p.endsWith('/total-leads/cold')) return ['Cold'];
    if (p.includes('/builder/sales/total-leads')) return [];
    return statusFilter;
  }, [location.pathname, statusFilter]);

  const [OpenAccommodation, setOpenAccommodation] = useState(false);
  const [isOpenAddDetailsModal, setIsOpenAddDetailsModal] = useState(false);
  const [isOpenBlockDetailingModal, setIsOpenBlockDetailingModal] = useState(false);
  const [isOpenUnitDetailingModal, setIsOpenUnitDetailingModal] = useState(false);
  const [isOpenImportAccommodation, setisOpenImportAccommodation] = useState(false);
  const [isOpenEditAccommodation, setIsOpenEditAccommodation] = useState(false);
  /** 'dashboard' | 'leads' | 'progression' — which view sits under the Sales header */
  const [salesSubView, setSalesSubView] = useState(() =>
    typeof window !== 'undefined' && window.location.pathname.includes('/builder/sales/total-leads')
      ? 'leads'
      : 'dashboard'
  );
  const [dashboardFilters, setDashboardFilters] = useState({
    projectID: projectIDFromState,
    month: location.state?.month || null
  });
  const hasDashboardProjectSelected =
    dashboardFilters.projectID !== null &&
    dashboardFilters.projectID !== undefined &&
    String(dashboardFilters.projectID) !== '';
  const resolvedDashboardProjectId = hasDashboardProjectSelected
    ? Number(dashboardFilters.projectID)
    : null;

  useEffect(() => {
    if (!showProgressionTab && salesSubView === 'progression') {
      setSalesSubView('dashboard');
    }
  }, [showProgressionTab, salesSubView]);

  const { data: onboardingResponse } = useQuery({
    queryKey: ['salesOnboardingMap', resolvedDashboardProjectId, salesUserId],
    queryFn: () => getSalesOnboardingMap(resolvedDashboardProjectId, salesUserId),
    enabled: showProgressionTab && !!resolvedDashboardProjectId && !!salesUserId
  });

  const progressionPendingCount = useMemo(() => {
    const progress = onboardingResponse?.data?.progress;
    if (progress?.total != null && progress?.completed != null) {
      return Math.max(0, progress.total - progress.completed);
    }

    return (onboardingResponse?.data?.steps || []).filter(
      (step) => step.status !== 'completed'
    ).length;
  }, [onboardingResponse]);

  const handleDashboardFiltersChange = useCallback(({ projectID, month }) => {
    setDashboardFilters({ projectID, month });
  }, []);

  const getSalesTabLabel = useCallback(
    (tab) => {
      if (tab === 'Progression' && hasDashboardProjectSelected) {
        return `Progression (${String(progressionPendingCount).padStart(2, '0')})`;
      }
      return tab;
    },
    [hasDashboardProjectSelected, progressionPendingCount]
  );

  useEffect(() => {
    if (location.pathname.includes('/builder/sales/total-leads')) {
      setSalesSubView('leads');
      setSecondaryTab('Leads');
      saveSecondaryTab('Leads');
    }
  }, [location.pathname]);

  useEffect(() => {
    const secondaryTabFromState = location.state?.secondaryTab;
    if (secondaryTabFromState) {
      setSecondaryTab(secondaryTabFromState);
      saveSecondaryTab(secondaryTabFromState);
      setSalesSubView('leads');
      return;
    }

    const statusFromState = location.state?.status;
    if (statusFromState) {
      // Set the tab to 'Leads' and set the status filter
      setSecondaryTab('Leads');
      setStatusFilter(statusFromState);
      saveSecondaryTab('Leads');
    }
  }, [location.state]);

  /* 
        Meta data
      */
  const tabbedComponentMetaData = {
    tabs: ['Details', 'Documents', 'Schedule']
  };

  // const secondaryTabbedComponentMetaData = {
  //   tabs: ['Leads','Under Consideration', 'Closed Customers', 'Invoices', 'Payment', 'Consolidated View of Sales']
  // };

  const salesSecondaryTabs = [
    'Prelead List',
    'Leads',
    'Unit Inventory',
    'Targets & Incentives',
    'Property Templates',
    ...(showProgressionTab ? ['Progression'] : []),
    ...(isManagementVertical ? ['Revenue'] : [])
  ];
  const unifiedSalesTabs = ['Dashboard', ...salesSecondaryTabs];

  /* 
        Event handlers
      */
  const handleChangePrimaryTab = useCallback(
    (tabName) => {
      setPrimaryTab(tabName);
      savePrimaryTab(tabName); // Save to localStorage when tab changes

      // Track primary tab change
      mixpanel.track('Sales Main Primary Tab Changed', {
        user_id: userId,
        tab: tabName,
        project_id: projectId,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectId]
  );

  const handleChangeSecondaryTab = useCallback(
    (tabName) => {
      setSecondaryTab(tabName);
      saveSecondaryTab(tabName); // Save to localStorage when secondary tab changes

      // Track secondary tab change
      mixpanel.track('Sales Main Secondary Tab Changed', {
        user_id: userId,
        tab: tabName,
        project_id: projectId,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectId]
  );

  /* 
        Effects
      */
  useEffect(() => {
    if (isOpenAddDetailsModal) {
      // console.log('Preventing scroll');
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpenAddDetailsModal]);

  /* 
        JSX
      */
  return (
    <>
      {/* Main content */}
      <Layout>
        {/* Header */}
        <div
          className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
    ${collapsed ? 'left-[60px] w-[calc(100%-60px)]' : 'left-[15rem] w-[calc(100%-16rem)]'}
  `}>
          <Header data={{ name: `` }} backArrow={false}>
            <div className="flex min-w-0 items-center gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar py-1">
              {unifiedSalesTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    if (tab === 'Dashboard') {
                      setSalesSubView('dashboard');
                      return;
                    }
                    if (tab === 'Progression') {
                      setSalesSubView('progression');
                      return;
                    }
                    setSalesSubView('leads');
                    navigate('/builder/sales/');
                    handleChangeSecondaryTab(tab);
                  }}
                  className={salesHeaderNavClass(
                    tab === 'Dashboard'
                      ? salesSubView === 'dashboard'
                      : tab === 'Progression'
                        ? salesSubView === 'progression'
                        : salesSubView === 'leads' && secondaryTab === tab
                  )}>
                  {getSalesTabLabel(tab)}
                </button>
              ))}
            </div>
          </Header>
        </div>

        {/* Primary Tabbed content */}
        {/* <div className="w-full fixed top-[4rem] z-50 bg-orange-600">
                    <PrimaryTabbedComponent
                        width={10}
                        data={tabbedComponentMetaData}
                        activePrimary={primaryTab}
                        onChangePrimaryTab={(tab) => handleChangePrimaryTab(tab)}
                    />
                </div> */}

        {showProgressionTab && salesSubView === 'progression' && (
          <SalesProgressionTab
            filters={dashboardFilters}
            onFiltersChange={handleDashboardFiltersChange}
          />
        )}

        {salesSubView === 'dashboard' && (
          <SalesDashboard
            skipLayout
            skipHeader
            projectID={dashboardFilters.projectID}
            month={dashboardFilters.month}
            onFiltersChange={handleDashboardFiltersChange}
          />
        )}

        {salesSubView === 'leads' && (
          <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
            {/* Overview content */}
            {secondaryTab === 'Prelead List' && <PreleadList />}

            {secondaryTab === 'Leads' && (
              <div className="w-full h-full">
                <SalesList
                  statusFilter={salesListStatusFilter}
                  forcedProjectID={projectIDFromState}
                  variant={isTotalLeadsRoute ? 'list' : 'metrics'}
                />
              </div>
            )}

            {secondaryTab === 'Unit Inventory' && (
              <div className="w-full h-full">
                <SalesUnitInventory
                  embedded
                  forcedProjectID={projectIDFromState}
                  openBulkUnitUpdate={location.state?.openBulkUnitUpdate}
                />
              </div>
            )}

            {secondaryTab === 'Under Consideration' && (
              <div className="w-full h-full mt-[4.4rem]">
                <UnderConsiderationList forcedProjectID={projectIDFromState} />
              </div>
            )}

            {/* PLC content */}
            {primaryTab === 'Details' && secondaryTab === `Closed Won` && (
              <div className=" w-full h-full ">
                <CustomerList forcedProjectID={projectIDFromState} />
              </div>
            )}

            {primaryTab === 'Details' && secondaryTab === `Revenue` && (
              <div className=" w-full h-full ">
                <ConsolidatedList />
              </div>
            )}

            {secondaryTab === 'Targets & Incentives' && <TargetsAndIncentives embedded={true} />}

            {secondaryTab === 'Property Templates' && (
              <div className="w-full min-h-full bg-gray-100 mt-[4.4rem]">
                <PropertyDocuments from={'sales'} />
              </div>
            )}
          </div>
        )}
      </Layout>
    </>
  );
};

export default SalesMain;
